import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, RefreshCw, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const FILTERS = [
  { key: "all", label: "All", roles: ["owner", "admin", "technician"] },
  { key: "owner", label: "Owner", roles: ["owner"] },
  { key: "admin", label: "Admin", roles: ["admin"] },
  { key: "owner_admin", label: "Owner / Admin", roles: ["owner", "admin"] },
  { key: "tech", label: "Tech", roles: ["technician"] },
  { key: "tech_admin", label: "Tech / Admin", roles: ["technician", "admin"] },
];

function timeAgo(iso) {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function TechMap() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const [orders, setOrders] = useState([]);
  const [staffLive, setStaffLive] = useState([]);
  const [userRoleById, setUserRoleById] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [o, u, l] = await Promise.all([
        api.get("/orders"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/users/locations").catch(() => ({ data: [] })),
      ]);
      setOrders(o.data);
      setStaffLive(l.data || []);
      const m = {};
      (u.data || []).forEach((x) => { m[x.user_id] = x.role; });
      setUserRoleById(m);
      setUpdatedAt(new Date());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Init map
  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([34.0, -84.5], 6);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
  }, []);

  const visible = useMemo(() => {
    const cfg = FILTERS.find((f) => f.key === filter) || FILTERS[0];
    // Job-attached pins (orders with tech_location)
    const jobPins = orders
      .filter((o) => {
        if (!o.tech_location) return false;
        const role = userRoleById[o.assigned_user_id];
        if (!role) return filter === "all";
        return cfg.roles.includes(role);
      })
      .map((o) => ({
        kind: "job",
        key: `job-${o.id}`,
        lat: o.tech_location.lat,
        lng: o.tech_location.lng,
        updated_at: o.tech_location.updated_at,
        name: o.assigned_user_name || "Tech",
        role: userRoleById[o.assigned_user_id] || "—",
        sub: `${o.customer_name || ""} — ${o.shipping_address || ""}`,
        status: o.status,
      }));
    // Live staff pins (anyone signed in within ~10 min) — dedupe per user (latest only)
    const livePins = staffLive
      .filter((u) => cfg.roles.includes(u.role))
      .map((u) => ({
        kind: "live",
        key: `live-${u.user_id}`,
        lat: u.last_location.lat,
        lng: u.last_location.lng,
        updated_at: u.last_location.updated_at,
        name: u.name || u.email,
        role: u.role,
        sub: "Signed in (live phone)",
        status: "online",
      }));
    return [...jobPins, ...livePins];
  }, [orders, staffLive, userRoleById, filter]);

  const counts = useMemo(() => {
    const c = {};
    FILTERS.forEach((f) => {
      const job = orders.filter((o) => {
        if (!o.tech_location) return false;
        const role = userRoleById[o.assigned_user_id];
        if (!role) return f.key === "all";
        return f.roles.includes(role);
      }).length;
      const live = staffLive.filter((u) => f.roles.includes(u.role)).length;
      c[f.key] = job + live;
    });
    return c;
  }, [orders, staffLive, userRoleById]);

  // Plot markers when visible changes
  useEffect(() => {
    if (!mapRef.current || !layerRef.current) return;
    layerRef.current.clearLayers();
    const points = [];
    visible.forEach((p) => {
      const live = p.kind === "live";
      const color = live ? "#0891b2" : "#1d4ed8";
      const icon = L.divIcon({
        className: "custom-pin",
        html: `<div style="background:${color};width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.2);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      const marker = L.marker([p.lat, p.lng], { icon }).addTo(layerRef.current);
      marker.bindPopup(`
        <div style="font-family:Manrope,sans-serif;min-width:200px">
          <div style="font-weight:700;font-size:14px;color:#0f172a">${p.name}</div>
          <div style="font-size:11px;color:#0891b2;text-transform:uppercase;letter-spacing:1px;margin-top:1px">${p.role}${live ? " · LIVE" : ""}</div>
          <div style="font-size:12px;color:#475569;margin-top:6px">${p.sub}</div>
          <div style="font-size:11px;color:#64748b;margin-top:2px">Status: ${p.status}</div>
          <div style="font-size:11px;color:#64748b">Updated ${timeAgo(p.updated_at)}</div>
          <a href="https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}" target="_blank" rel="noreferrer" style="display:inline-block;margin-top:6px;font-size:11px;color:#0891b2">Open in Google Maps ↗</a>
        </div>
      `);
      points.push([p.lat, p.lng]);
    });
    if (points.length === 1) mapRef.current.setView(points[0], 14);
    else if (points.length > 1) mapRef.current.fitBounds(points, { padding: [40, 40] });
  }, [visible]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 inline-flex items-center gap-2">
            <MapIcon className="h-6 w-6 text-cyan-700" /> Map
          </h1>
          <p className="mt-1 text-sm text-slate-500">{visible.length} active GPS{visible.length === 1 ? "" : "s"} in view</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">Updated {updatedAt ? timeAgo(updatedAt.toISOString()) : "—"} · auto 30s</span>
          <Button variant="outline" size="sm" onClick={load} data-testid="map-refresh-button"><RefreshCw className="h-4 w-4" /> Refresh</Button>
        </div>
      </div>

      {/* Role filter tabs */}
      <div className="mt-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition ${active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}
              data-testid={`map-filter-${f.key}`}
            >
              {f.label}
              <span className={`rounded-full px-1.5 text-[11px] font-semibold ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>{counts[f.key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-12">
        <div className="dash-surface overflow-hidden lg:col-span-8" data-testid="map-container">
          <div ref={mapEl} className="h-[560px] w-full" />
        </div>
        <div className="dash-surface p-5 lg:col-span-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><UsersIcon className="h-3.5 w-3.5" /> Active</div>
          <ul className="mt-3 space-y-2">
            {visible.length === 0 && (
              <li className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
                Nobody pinned in this filter yet.
              </li>
            )}
            {visible.map((p) => (
              <li key={p.key} className="rounded-md border border-slate-200 bg-white p-3" data-testid={`map-list-${p.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-cyan-700">{p.role}{p.kind === "live" ? " · LIVE" : ""}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${p.kind === "live" ? "bg-cyan-100 text-cyan-800" : "bg-blue-100 text-blue-800"}`}>{p.status}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500 truncate">{p.sub}</div>
                <div className="mt-1 text-[11px] text-slate-400">Updated {timeAgo(p.updated_at)}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
